<template>
  <div :data-view-id="uniqueTitle" :unique-title="uniqueTitle">
    <ViewTitle :title="title" />
    <!-- OAuth provider buttons -->
    <div v-if="phase === 'credentials' && oauthProviders.length" class="oauth-buttons mb-3">
      <b-button
        v-for="provider in oauthProviders"
        :key="provider.id"
        variant="outline-primary"
        class="oauth-btn mb-2 w-100"
        @click="startOAuthLogin(provider)"
      >
        Sign in with {{ provider.displayName }}
      </b-button>
      <div v-if="loginFields.length" class="oauth-divider my-3">
        <span>or</span>
      </div>
    </div>

    <!-- Username/password form -->
    <b-form v-if="phase === 'credentials' && loginFields.length" @submit="onSubmit" :id="uniqueTitle" novalidate>
      <div v-for="field in loginFields" :key="field.uniqueTitle" class="mb-4">
        <b-form-group :label="field.label" :label-for="field.uniqueTitle">
          <b-form-input
            :id="field.uniqueTitle"
            :name="field.uniqueTitle"
            :type="field.isPassword ? 'password' : 'text'"
            :autocomplete="field.isPassword ? 'current-password' : 'username'"
            :placeholder="field.hint || undefined"
            v-model="fieldValues[field.uniqueTitle]"
          />
          <small v-if="field.description" class="text-muted d-block mt-1">{{ field.description }}</small>
        </b-form-group>
      </div>
      <label v-if="rememberAllowed" class="remember-me mb-3">
        <input type="checkbox" v-model="rememberMe" />
        Remember me
      </label>
      <div class="mb-3">
        <button type="button" class="btn btn-link p-0" @click="toggleForgot">Forgot password?</button>
      </div>
      <div v-if="showForgot" class="mb-3">
        <b-form-group label="Sign-in name" label-for="forgot-name">
          <b-form-input id="forgot-name" v-model="forgotName" autocomplete="username" />
        </b-form-group>
        <b-button type="button" variant="outline-primary" :disabled="forgotBusy" @click="sendReset">Email a reset link</b-button>
        <div v-if="forgotMessage" class="mt-2" role="status">{{ forgotMessage }}</div>
      </div>
      <b-button type="submit" variant="primary" :class="submittedClass">{{submitText}}</b-button>
      <div v-if="errorMessage" class="text-danger mt-2" role="alert">{{errorMessage}}</div>
    </b-form>
    <AuthenticatorPanel
      v-else-if="phase !== 'credentials'"
      :key="phase"
      :phase="phase"
      :otpauth-url="otpauthUrl"
      :secret="setupSecret"
      :recovery-codes="recoveryCodes"
      :error-message="errorMessage"
      :busy="submittedClass === 'disabled'"
      @submit="submitFactor"
      @continue="finishPending"
    />
  </div>
</template>

<script>

  import axios from 'axios'
  import ViewTitle from './ViewTitle.vue'
  import AuthenticatorPanel from './AuthenticatorPanel.vue'
  import { fieldNameAndHint } from '../../../shared/form-field-label.js'
  import { userSessionForRead } from '../../../shared/user-session-policy.js'
  import { completeSignIn } from '../lib/complete-sign-in'
  import { writeLoggedInUser } from '../lib/live-user-storage'
  import { pageHostUniqueTitle } from '../lib/page-host'

  function extractLoginFields (formComponents) {

    const fields = []

    const search = (components) => {

      if (!components) return

      for (const comp of components) {

        if (comp.type === 'formInput' || comp.type === 'formPassword') {

          const named = fieldNameAndHint(comp.title, comp.placeholder || comp.uniqueTitle)

          fields.push({
            uniqueTitle: comp.uniqueTitle,
            label: named.label || comp.uniqueTitle,
            hint: named.placeholder,
            description: comp.description || '',
            isPassword: comp.type === 'formPassword'
          })
        }

        if (comp.components) {

          search(comp.components)
        }
      }
    }

    search(formComponents)

    return fields
  }

  export default {
    components: {
      ViewTitle,
      AuthenticatorPanel
    },
    props: ['formComponents', 'uniqueTitle', 'title', 'submissionRules', 'twoFactor'],
    data: function () {

      return {
        loginFields: [],
        fieldValues: {},
        submittedClass: ``,
        submitText: `login`,
        errorMessage: ``,
        oauthProviders: [],
        phase: `credentials`,
        setupToken: ``,
        challengeToken: ``,
        setupSecret: ``,
        otpauthUrl: ``,
        recoveryCodes: [],
        pendingSession: null,
        rememberMe: false,
        rememberAllowed: false,
        showForgot: false,
        forgotName: '',
        forgotMessage: '',
        forgotBusy: false
      }
    },
    watch: {
      formComponents: {
        immediate: true,
        handler () {

          this.loginFields = extractLoginFields(this.formComponents)

          const values = {}

          for (const field of this.loginFields) {

            values[field.uniqueTitle] = ``
          }

          this.fieldValues = values
        }
      }
    },
    async mounted () {
      await this.loadOAuthProviders()
      await this.loadSignInOptions()
    },
    methods: {
      async loadSignInOptions () {

        try {

          const account = this.routeAccount
          const application = this.$route.params.application
          const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/settings`)
          const session = userSessionForRead(res.data && res.data.userSession)

          this.rememberAllowed = session.inactivityMinutes == null
        } catch (err) {

          this.rememberAllowed = false
        }
      },
      toggleForgot () {

        this.showForgot = !this.showForgot

        if (!this.showForgot) return

        const username = this.loginFields.find((field) => !field.isPassword)

        if (username && !this.forgotName) this.forgotName = this.fieldValues[username.uniqueTitle] || ''
      },
      async sendReset () {

        if (this.forgotBusy) return

        const username = this.loginFields.find((field) => !field.isPassword)

        if (!username) return

        const name = String(this.forgotName || this.fieldValues[username.uniqueTitle] || '').trim()

        if (!name) {

          this.forgotMessage = 'Enter the sign-in name.'

          return
        }

        this.forgotBusy = true
        this.forgotMessage = ''

        try {

          await axios.post(`${VUE_APP_PROXY_SERVER_URL}/${this.routeAccount}/${this.$route.params.application}/password/forgot`, {
            pageUniqueTitle: pageHostUniqueTitle(this.$el),
            componentUniqueTitle: this.uniqueTitle,
            [username.uniqueTitle]: name
          })
          this.forgotMessage = 'If that name has an email address, a reset link is on its way.'
        } catch (err) {

          this.forgotMessage = 'The reset email could not be sent.'
        } finally {

          this.forgotBusy = false
        }
      },
      async loadOAuthProviders () {
        try {
          const account = this.routeAccount
          const application = this.$route.params.application
          const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/oauth/providers`)
          this.oauthProviders = res.data || []
        } catch (err) {
          console.log('failed to load oauth providers', err)
        }
      },
      async startOAuthLogin (provider) {
        if (window.__superturbo_preview_mode) return

        const account = this.routeAccount
        const application = this.$route.params.application

        try {

          const response = await axios.post(
            `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/oauth/start`,
            {
              providerId: provider.id,
              returnPath: window.location.pathname + window.location.search
            }
          )

          if (response.data && response.data.authorizationUrl) {

            window.location.href = response.data.authorizationUrl
          }
        } catch (err) {

          this.errorMessage = 'OAuth login could not start'
        }
      },
      async finishSession (data) {

        const redirectPath = await completeSignIn({
          username: data.username,
          userSession: data.userSession,
          submissionRules: this.submissionRules,
          fieldValues: this.fieldValues,
          modalRoute: this.modalRoute,
          account: this.routeAccount,
          application: this.$route.params.application,
          path: this.routePath || this.$route.path,
          accessKey: this.routeAccessKey,
          embed: this.routeEmbed,
          router: this.$router
        })

        if (redirectPath) return

        this.submittedClass = ``
        this.submitText = `logged in`
      },
      async finishPending () {

        if (!this.pendingSession) return

        await this.finishSession(this.pendingSession)
      },
      holdRecoveryCodes (data) {

        writeLoggedInUser(data.username, {
          account: this.routeAccount,
          application: this.$route.params.application
        })
        this.pendingSession = data
        this.recoveryCodes = data.recoveryCodes
        this.phase = `recovery`
        this.submittedClass = ``
        this.errorMessage = ``
      },
      async onSubmit (event) {

        event.preventDefault()

        if (window.__superturbo_preview_mode) return

        if (this.submittedClass === `disabled`) {

          return
        }

        this.errorMessage = ``
        this.submittedClass = `disabled`
        this.submitText = `logging in...`

        const pageUniqueTitle = pageHostUniqueTitle(this.$el)

        const body = {
          pageUniqueTitle,
          componentUniqueTitle: this.uniqueTitle
        }

        for (const field of this.loginFields) {

          body[field.uniqueTitle] = this.fieldValues[field.uniqueTitle]
        }

        if (this.rememberAllowed && this.rememberMe) body.rememberMe = true

        try {

          const response = await axios.post(`${VUE_APP_PROXY_SERVER_URL}/${this.routeAccount}/${this.$route.params.application}/login`, body)

          if (response.data && response.data.twoFactorSetup) {

            this.phase = `setup`
            this.setupToken = response.data.setupToken
            this.setupSecret = response.data.secret
            this.otpauthUrl = response.data.otpauthUrl
            this.submittedClass = ``
            this.submitText = `login`
            return
          }

          if (response.data && response.data.twoFactorRequired) {

            this.phase = `challenge`
            this.challengeToken = response.data.challengeToken
            this.submittedClass = ``
            this.submitText = `login`
            return
          }

          await this.finishSession(response.data)
        } catch (error) {

          console.log(`login error:`)
          console.log(error)

          this.submittedClass = ``
          this.submitText = `login`

          if (error.response && error.response.data && error.response.data.error) {

            this.errorMessage = error.response.data.error
          } else {

            this.errorMessage = `Login failed`
          }
        }
      },
      async submitFactor (code) {

        if (window.__superturbo_preview_mode) return
        if (this.submittedClass === `disabled`) return

        this.errorMessage = ``
        this.submittedClass = `disabled`

        const setup = this.phase === `setup`
        const path = setup ? `login/authenticator/setup` : `login/authenticator`
        const body = setup
          ? { setupToken: this.setupToken, code }
          : { challengeToken: this.challengeToken, code }

        if (this.rememberAllowed && this.rememberMe) body.rememberMe = true

        try {

          const response = await axios.post(
            `${VUE_APP_PROXY_SERVER_URL}/${this.routeAccount}/${this.$route.params.application}/${path}`,
            body
          )

          if (response.data && response.data.recoveryCodes && response.data.recoveryCodes.length) {

            this.holdRecoveryCodes(response.data)
            return
          }

          await this.finishSession(response.data)
        } catch (error) {

          this.submittedClass = ``
          this.errorMessage = (error.response && error.response.data && error.response.data.error) || `That code is not valid`
        }
      }
    }
  }
</script>

<style scoped>
.oauth-buttons {
  display: flex;
  flex-direction: column;
}

.oauth-btn {
  font-weight: 500;
}

.oauth-divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: #999;
  font-size: 0.85rem;
}

.oauth-divider::before,
.oauth-divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #dee2e6;
}

.oauth-divider span {
  padding: 0 10px;
}

.remember-me {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
</style>
